/* eslint-disable jsdoc/require-param, jsdoc/check-param-names */
import { Children } from 'react';
import { OptimizelyFeature, OptimizelyProvider } from '@optimizely/react-sdk';

import { applyPropsToChildren } from '#src/lib/apply-props-to-children';
import { isServer } from '#src/lib/utils.mjs';

import { events } from './events';
import { optimizelyClient } from './client';

export const ExperimentationProvider = ({ children }) => (
  <OptimizelyProvider optimizely={optimizelyClient} isServerSide={isServer}>
    {children}
  </OptimizelyProvider>
);

/**
 * Declare that a section of code contains an experiment. On render, the
 * "experiment activated" event triggers (Segment group call) to let us know
 * which experiment variation a user gets assigned to.
 *
 * @param {object} props
 * @param {string} props.experiment - The name of the experiment.
 * @returns {JSX.Element} React component.
 */
export const Experiment = ({ children, experiment }) => {
  if (!experiment) {
    throw new Error(
      `'Experiment' component must have 'experiment' prop (string).`
    );
  }

  return (
    <OptimizelyFeature feature={experiment} autoUpdate={true}>
      {(isEnabled, variables) => {
        // isEnabled informs us which variation the user was was assigned to,
        // the "test" variation ("On" in Optimizely) or "control" aka "Off".
        const variation = isEnabled ? 'test' : 'control';
        events.experimentActivated({ experiment, variables, variation });
        // Pass the experiment, variation and experiment variables on down to
        // all children in the tree.
        return Children.map(children, (child) =>
          applyPropsToChildren(child, {
            experiment,
            variation,
            ...variables,
          })
        );
      }}
    </OptimizelyFeature>
  );
};

/**
 * Declare that a section of code belongs to a specific varation of an
 * experiment (either "control" or "test"). The original code should be wrapped
 * in the "control" variation. Code under test should be wrapped in "test"
 * variation. Code beneath a variation that the user is not assigned to will
 * not render. The rendered variation triggers the "experiment variation viewed"
 * event (Segment event call).
 *
 * @param {object} props
 * @param {string} props.name - The name of the varation (either "test" or
 * "control").
 * @returns {JSX.Element} React component.
 */
export const ExperimentVariation = ({
  // 'name' should be passed explicitly.
  name,
  // The rest of these are generated programmatically
  children,
  experiment,
  variation,
  ...variables
}) => {
  if (!experiment) {
    throw new Error(
      `'ExperimentVariation' components must have a parent 'Experiment' somewhere in the tree.`
    );
  }

  if (name !== variation) {
    return null;
  }

  events.experimentVariationViewed({
    experiment,
    variables,
    variation,
  });

  return children;
};

export const Feature = ({ children, feature }) => (
  <OptimizelyFeature autoUpdate={true} feature={feature}>
    {(isEnabled, variables) =>
      isEnabled &&
      // Pass the experiment variables down to all children in the tree.
      Children.map(children, (child) => applyPropsToChildren(child, variables))
    }
  </OptimizelyFeature>
);
